import React, { ComponentProps, FC, memo, useMemo } from 'react'

import { Carousel } from '@factorialco/f0-react/dist/experimental'
import { Celebration as BaseCelebration } from '@factorialco/f0-react/dist/experimental'
import { WeeklyCelebrationsDocument } from 'generated/resources/graphql'
import moment from 'moment'
import { useDebounceValue } from 'usehooks-ts'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString } from 'modules/core/lib/dates'
import { Widget, useWidgetsContext } from 'modules/core/lib/widgets'

import { buildHolidaysWidget } from 'modules/dashboard/components/Widgets/Holidays'

import Celebration from './Celebration'

const DEBOUNCE_LOADING_TIME = 500

const COLUMNS = {
  default: 1,
  lg: 4,
  md: 4,
  sm: 3,
  xs: 2,
} as const

const DOUBLE_COLUMNS: ComponentProps<typeof Carousel>['doubleColumns'] = [
  { index: 0, sizes: ['default', 'md', 'lg'] },
]

type Props = {
  timeoffModuleEnabled: boolean
}
const WeeklyCelebrationsCarousel: FC<Props> = ({ timeoffModuleEnabled }) => {
  const { access, employee } = useCurrentSession()
  const startsOn = moment().utc(false).startOf('day')
  const endsOn = startsOn.clone().add(7, 'days')

  const { widgetStates } = useWidgetsContext()

  const isHolidaysWidgetEmpty = widgetStates?.['holidays'] === 'empty_and_not_shown'

  const today = i18nDateToString(moment(), 'backend')

  const { data, loading } = useQuery(WeeklyCelebrationsDocument, {
    variables: {
      accessId: access.id,
      startsOn: startsOn.toISOString(),
      endsOn: endsOn.toISOString(),
    },
  })

  const holidaysWidgetArgs = useMemo(
    () => ({ today, employeeId: employee?.id ?? '-1', skipLeaves: !timeoffModuleEnabled }),
    [today, employee?.id, timeoffModuleEnabled]
  )

  const celebrationIds =
    data?.calendar.calendarEventsConnection.nodes.map((celebration) => celebration.id) ?? []

  const HolidaysWidget = isHolidaysWidgetEmpty ? null : (
    <ErrorBoundary customCrashComponent={<></>}>
      <Widget widget={buildHolidaysWidget()} args={holidaysWidgetArgs} fullHeight />
    </ErrorBoundary>
  )

  const [showLoader] = useDebounceValue(!celebrationIds.length && loading, DEBOUNCE_LOADING_TIME)

  if (showLoader)
    return (
      <Carousel columns={COLUMNS}>
        {new Array(8).fill(null).map((_, i) => (
          <BaseCelebration.Skeleton key={i} />
        ))}
      </Carousel>
    )

  return (
    <Carousel columns={COLUMNS} doubleColumns={isHolidaysWidgetEmpty ? undefined : DOUBLE_COLUMNS}>
      {HolidaysWidget}
      {celebrationIds.map((celebrationId, index) => (
        <Celebration key={celebrationId} id={celebrationId} index={index} />
      ))}
    </Carousel>
  )
}

export default memo(WeeklyCelebrationsCarousel)
